import React from 'react'
import PropTypes from 'prop-types'
import { Box, Button, Chip, CircularProgress, Grid, List, ListItemButton, ListItemText, Stack, TextField, Typography } from '@mui/material'
import FeedIcon from '@mui/icons-material/Feed'
import MosSmartDialog from '../../../common/MosSmartDialog'
import WorkerRequiredButton from '../../../common/WorkerRequiredButton'
import createDialogTitle from '../../../../../generic/utilities/dialogUtil'
import { useSmartTranslation } from '../../../../../generic/hooks/useSmartTranslation'

const IncidenceCreateDialog = ({
  open,
  onClose,
  isTouchLayout,
  saving,
  catalogLoading,
  catalog,
  incidenceGroups,
  visibleIncidenceGroups,
  normalizedSearch,
  searchText,
  onSearchTextChange,
  selectedTypeName,
  selectedIncidenceName,
  selectedIncidenceObj,
  onSelectIncidence,
  comment,
  onCommentChange,
  displayedError,
  displayedErrorMessage,
  onCreate
}) => {
  const { t_ } = useSmartTranslation()

  return (
    <MosSmartDialog
      cancel={false}
      accept={false}
      title={createDialogTitle({ color: 'primary', title: t_('CREATE INCIDENCE'), icon: FeedIcon })}
      setOpen={open}
      minWidth={isTouchLayout ? 'calc(100vw - 24px)' : '42rem'}
      paperWidth={isTouchLayout ? 'calc(100vw - 24px)' : '52rem'}
      renderCustomButtons={
        <>
          <Button
            variant='outlined'
            color='inherit'
            size='large'
            onClick={onClose}
            sx={{ minHeight: 48, px: 2.5 }}
          >
            {t_('CANCEL')}
          </Button>
          <WorkerRequiredButton
            color='primary'
            variant='contained'
            size='large'
            onClick={onCreate}
            disabled={saving || catalogLoading || !selectedIncidenceObj}
            sx={{ minHeight: 48, px: 3 }}
          >
            {saving ? t_('SAVING...') : t_('ACCEPT')}
          </WorkerRequiredButton>
        </>
      }
    >
      <Stack spacing={2} sx={{ p: (theme) => theme.spacing(2) }}>
        <Box
          sx={{
            p: 2,
            bgcolor: 'action.hover',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider'
          }}
        >
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} md={6}>
              <Stack direction='row' spacing={1} alignItems='center' justifyContent={{ xs: 'center', md: 'flex-start' }} flexWrap='wrap'>
                <Typography variant='caption' color='text.secondary'>
                  {t_('Type')}
                </Typography>
                <Chip
                  label={selectedTypeName || t_('Not selected')}
                  variant={selectedTypeName ? 'filled' : 'outlined'}
                  color={selectedTypeName ? 'primary' : 'default'}
                  sx={{ fontWeight: 600 }}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction='row' spacing={1} alignItems='center' justifyContent={{ xs: 'center', md: 'flex-end' }} flexWrap='wrap'>
                <Typography variant='caption' color='text.secondary'>
                  {t_('Incidence')}
                </Typography>
                <Chip
                  label={selectedIncidenceName || t_('Not selected')}
                  variant={selectedIncidenceName ? 'filled' : 'outlined'}
                  color={selectedIncidenceName ? 'primary' : 'default'}
                  sx={{ fontWeight: 600 }}
                />
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {catalogLoading && (
          <Stack direction='row' spacing={1} alignItems='center' justifyContent='center' sx={{ py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant='body2'>{t_('Loading...')}</Typography>
          </Stack>
        )}

        {!catalogLoading && Array.isArray(catalog) && catalog.length === 0 && (
          <Typography color='text.secondary' sx={{ py: 2 }}>
            {t_('No incidences available')}
          </Typography>
        )}

        {!catalogLoading && incidenceGroups.length > 0 && (
          <Stack spacing={1.5}>
            <TextField
              value={searchText}
              onChange={(e) => onSearchTextChange(e.target.value)}
              label={t_('Search')}
              placeholder={t_('Search incidences...')}
              fullWidth
              variant='filled'
              disabled={saving}
              inputProps={{ inputMode: 'search', 'aria-label': t_('Search incidences') }}
            />

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: 1.5,
                overflowX: 'auto',
                overflowY: 'hidden',
                pb: 1,
                WebkitOverflowScrolling: 'touch'
              }}
              role='group'
              aria-label={t_('Incidence types and incidences')}
            >
              {visibleIncidenceGroups.map(({ type, filteredItems }) => {
                const typeName = type?.name ?? ''
                return (
                  <Box
                    key={type?.id || typeName}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 2,
                      overflow: 'hidden',
                      bgcolor: 'background.paper',
                      flex: '0 0 auto',
                      width: { xs: 280, sm: 320, md: 320 },
                      minWidth: { xs: 280, sm: 320, md: 280 }
                    }}
                  >
                    <Box
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        bgcolor: 'background.paper',
                        px: 1.5,
                        py: 1.25,
                        borderBottom: 1,
                        borderColor: 'divider'
                      }}
                    >
                      <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
                        <Typography sx={{ fontWeight: 800, textAlign: 'left' }}>{typeName}</Typography>
                        <Chip size='small' label={filteredItems.length} variant='outlined' sx={{ fontWeight: 700 }} />
                      </Stack>
                    </Box>

                    <Box sx={{ maxHeight: isTouchLayout ? '46vh' : 420, overflowY: 'auto' }}>
                      <List disablePadding sx={{ p: 1.25, display: 'grid', gap: 1 }}>
                        {filteredItems.map((inc) => {
                          const isSelected = selectedIncidenceObj?.id === inc.id
                          return (
                            <Box component='li' key={inc.id} sx={{ m: 0, p: 0, listStyle: 'none' }}>
                              <ListItemButton
                                selected={isSelected}
                                onClick={() => onSelectIncidence(inc, typeName)}
                                aria-label={`${typeName}: ${inc?.name ?? ''}`}
                                sx={{
                                  borderRadius: 2,
                                  border: 1,
                                  borderColor: isSelected ? 'primary.main' : 'divider',
                                  py: 1.25,
                                  px: 1.5,
                                  minHeight: 56,
                                  alignItems: 'flex-start',
                                  '&.Mui-selected': {
                                    bgcolor: 'action.selected'
                                  },
                                  '&.Mui-selected:hover': {
                                    bgcolor: 'action.selected'
                                  }
                                }}
                              >
                                <ListItemText
                                  primary={inc?.name}
                                  secondary={inc?.description || inc?.code || null}
                                  primaryTypographyProps={{ fontWeight: 700, sx: { textAlign: 'left' } }}
                                  secondaryTypographyProps={{
                                    sx: { textAlign: 'left' },
                                    noWrap: true
                                  }}
                                />
                              </ListItemButton>
                            </Box>
                          )
                        })}
                      </List>
                    </Box>
                  </Box>
                )
              })}

              {visibleIncidenceGroups.length === 0 && (
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    p: 2
                  }}
                >
                  <Typography color='text.secondary' sx={{ textAlign: 'left' }}>
                    {normalizedSearch ? t_('No results') : t_('No incidences available')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Stack>
        )}

        <TextField
          value={comment}
          label={t_('Comment')}
          placeholder={t_('Provide an optional comment...')}
          onChange={(e) => onCommentChange(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          maxRows={6}
          variant='filled'
          disabled={saving}
        />

        {displayedError && (
          <Typography color='error' variant='body2'>
            {displayedErrorMessage}
          </Typography>
        )}
      </Stack>
    </MosSmartDialog>
  )
}

IncidenceCreateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isTouchLayout: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  catalogLoading: PropTypes.bool.isRequired,
  catalog: PropTypes.array,
  incidenceGroups: PropTypes.array.isRequired,
  visibleIncidenceGroups: PropTypes.array.isRequired,
  normalizedSearch: PropTypes.string.isRequired,
  searchText: PropTypes.string.isRequired,
  onSearchTextChange: PropTypes.func.isRequired,
  selectedTypeName: PropTypes.string.isRequired,
  selectedIncidenceName: PropTypes.string.isRequired,
  selectedIncidenceObj: PropTypes.object,
  onSelectIncidence: PropTypes.func.isRequired,
  comment: PropTypes.string.isRequired,
  onCommentChange: PropTypes.func.isRequired,
  displayedError: PropTypes.any,
  displayedErrorMessage: PropTypes.string.isRequired,
  onCreate: PropTypes.func.isRequired
}

export default IncidenceCreateDialog
